import React from 'react';
import { RING, RING_STOPS } from '../../wall/wallTokens';
import PortalShader, { FRAGS } from './PortalShader';
import { backgroundOf } from '../../../utils/portalLayout';

/**
 * The fourteen animated backgrounds a text-only Portal can wear (handoff v3 §5.6). Six are
 * WebGL shaders (`PortalShader`, keyed by kind so every switch gets a fresh canvas); the rest
 * are the prototype's DOM recipes, translated: everything sits on #000, clipped, with scanlines
 * on top, and only transform / opacity / background-position move. PULSE is kept for Portals
 * sent before v3.
 *
 * A recipe here is a composited layer per moving part. That is what it costs, so keep the
 * parts small: TUNNEL's nine card-sized rings blew past the Android WebView's tile budget and
 * smeared, and it is a shader now (see `FRAGS.tunnel`).
 */
const abs = { position: 'absolute' };
const ring6 = { background: RING, backgroundSize: '200% 100%', animation: 'prcRingSlide 6s linear infinite' };

const SCAN = <div key="scan" aria-hidden style={{ ...abs, inset: 0, background: 'repeating-linear-gradient(0deg, rgba(255,255,255,.035) 0 1px, transparent 1px 3px)' }} />;

function Wrap({ children, kind, extra = '#000' }) {
  return (
    // data-kind, like the shader wrapper below: the share card asks the DOM which background
    // this is so it knows whether to film it. Without it every CSS background looked static and
    // a "video" of one came out frozen.
    <div aria-hidden data-testid="portal-background" data-kind={kind} style={{ ...abs, inset: 0, background: extra, overflow: 'hidden', pointerEvents: 'none' }}>
      {children}
      {SCAN}
    </div>
  );
}

const FLECKS = Array.from({ length: 26 }, (_, i) => ({
  x: (i * 37 + 11) % 100, y: (i * 53 + 7) % 92, s: 3 + (i * 7) % 5,
  c: [...RING_STOPS, '#FFFFFF'][i % 8], d: 6 + (i * 5) % 7, dl: -((i * 1.7) % 9),
}));

const RIGS = [
  { left: '82%', beams: [[62, 150, 12, 0.5, '#FD199F'], [40, 190, 14, 0.45, '#B5B1FF'], [20, 130, 10, 0.4, '#127BFD']] },
  { left: '14%', beams: [[-30, 170, 13, 0.45, '#22ECFB'], [-52, 120, 11, 0.4, '#FCAA6D'], [-12, 100, 15, 0.35, '#FF33C6']] },
];
const PLUSES = [[14, 18, '#FF33C6', 0], [80, 30, '#22ECFB', 1.1], [60, 10, '#FAE1B4', 2]];

function Stage() {
  return (
    <Wrap kind="stage">
      <div style={{ ...abs, inset: 0, background: 'linear-gradient(120deg, rgba(253,25,159,.14), rgba(18,123,253,.12), rgba(34,236,251,.1), rgba(252,170,109,.12))', backgroundSize: '300% 300%', animation: 'prcHue 14s ease-in-out infinite' }} />
      <div style={{ ...abs, left: '-20%', right: '-20%', top: '-30%', height: '70%', background: 'radial-gradient(50% 50% at 50% 40%, rgba(255,51,198,.28), rgba(18,123,253,.14) 50%, transparent 75%)', animation: 'prcGlow 5s ease-in-out infinite alternate' }} />
      {FLECKS.map((f, i) => (
        <span key={i} style={{ ...abs, left: `${f.x}%`, top: `${f.y}%`, width: f.s, height: f.s, background: f.c, opacity: 0, animation: `prcFleck ${f.d}s linear ${f.dl}s infinite` }} />
      ))}
      {RIGS.map((rig, j) => (
        <div key={j} style={{ ...abs, left: rig.left, top: -30, width: 0, height: 0 }}>
          {rig.beams.map(([r, w, d, o, c], i) => (
            <div
              key={i}
              style={{
                ...abs, left: -w / 2, top: 0, width: w, height: 1200, transformOrigin: '50% 0', '--r': `${r}deg`, opacity: o,
                mixBlendMode: 'screen', clipPath: 'polygon(46% 0, 54% 0, 100% 100%, 0 100%)',
                background: `linear-gradient(to bottom, ${c}, ${c}55 45%, ${c}00 90%)`,
                animation: `prcSweep ${d}s ease-in-out ${-(i + j * 2) * 2.5}s infinite alternate`,
              }}
            />
          ))}
        </div>
      ))}
      {PLUSES.map(([x, y, c, dl], i) => (
        <span key={i} style={{ ...abs, left: `${x}%`, top: `${y}%`, color: c, font: "700 26px/1 'JetBrains Mono', monospace", opacity: 0, animation: `prcTwinkle 2.6s ease-in-out ${dl}s infinite` }}>+</span>
      ))}
      <div style={{ ...abs, left: 0, right: 0, bottom: 0, height: 6, ...ring6 }} />
    </Wrap>
  );
}

const AURORA = [['#FD199F', '-20%', '-10%', '60px', '80px', 17], ['#127BFD', '40%', '20%', '-70px', '50px', 21], ['#22ECFB', '-10%', '50%', '80px', '-60px', 19], ['#FCAA6D', '45%', '65%', '-50px', '-80px', 23], ['#B5B1FF', '10%', '25%', '40px', '60px', 15]];
const BANDS = [[8, 5, '#22ECFB', 7], [19, 2, '#FD199F', 5], [31, 9, '#127BFD', 9], [47, 3, '#FAE1B4', 6], [58, 12, '#FF33C6', 11], [76, 4, '#B5B1FF', 8], [88, 7, '#FCAA6D', 10]];

/** `capture` (default true) keeps a shader's drawing buffer for the share sheet's read-back;
 * the page background passes false — nothing films it. */
export default function PortalBackground({ kind = 'lasers', capture = true }) {
  if (FRAGS[kind]) {
    return (
      <div aria-hidden data-testid="portal-background" data-kind={kind} style={{ ...abs, inset: 0, background: '#000', overflow: 'hidden', pointerEvents: 'none' }}>
        <PortalShader key={kind} frag={FRAGS[kind]} fallback={backgroundOf(kind).swatch} capture={capture} />
      </div>
    );
  }
  switch (kind) {
    case 'stage':
      return <Stage />;
    case 'haze':
      return (
        <Wrap kind={kind}>
          <div style={{ ...abs, left: '-30%', top: '-10%', width: '120%', height: '70%', background: 'radial-gradient(closest-side, rgba(181,177,255,.22), rgba(181,177,255,0))', '--dx': '90px', '--dy': '160px', animation: 'prcDrift 26s ease-in-out infinite' }} />
        </Wrap>
      );
    case 'dusk':
      return (
        <Wrap kind={kind}>
          <div style={{ ...abs, inset: 0, background: 'linear-gradient(to bottom, #000 0%, #07061a 45%, #1c0a26 80%, #2a0d22 100%)' }} />
          <div style={{ ...abs, left: 0, right: 0, bottom: 0, height: '40%', background: 'linear-gradient(to top, rgba(252,170,109,.18), rgba(252,170,109,0))', animation: 'prcGlow 8s ease-in-out infinite alternate' }} />
        </Wrap>
      );
    case 'aurora':
      return (
        <Wrap kind={kind}>
          {AURORA.map(([c, x, y, dx, dy, d], i) => (
            <div key={i} style={{ ...abs, left: x, top: y, width: '90%', height: '55%', background: `radial-gradient(closest-side, ${c}, ${c}00)`, opacity: 0.55, filter: 'blur(20px)', mixBlendMode: 'screen', '--dx': dx, '--dy': dy, animation: `prcDrift ${d}s ease-in-out ${-i * 3}s infinite` }} />
          ))}
        </Wrap>
      );
    case 'fleck':
      return (
        <Wrap kind={kind}>
          {Array.from({ length: 10 }, (_, i) => (
            <span key={i} style={{ ...abs, left: `${(i * 41 + 17) % 100}%`, top: `${(i * 29 + 9) % 90}%`, width: 3, height: 3, background: RING_STOPS[i % 7], opacity: 0, animation: `prcFleck ${10 + (i % 4) * 2}s linear ${-i * 1.3}s infinite` }} />
          ))}
        </Wrap>
      );
    case 'grid':
      return (
        <Wrap kind={kind}>
          <div style={{ ...abs, left: 0, right: 0, top: 0, height: '52%', background: 'linear-gradient(to bottom, #000 0%, #1a0630 55%, #3a0a4a 100%)' }} />
          <div style={{ ...abs, left: '-10%', right: '-10%', top: '44%', height: '16%', background: 'radial-gradient(50% 50% at 50% 60%, rgba(255,51,198,.55), transparent 75%)', animation: 'prcGlow 4s ease-in-out infinite alternate' }} />
          <div style={{ ...abs, left: 0, right: 0, top: '52%', height: 2, ...ring6 }} />
          <div style={{ ...abs, left: 0, right: 0, top: '52%', bottom: 0, perspective: 260, overflow: 'hidden' }}>
            <div style={{ ...abs, left: '-60%', right: '-60%', top: 0, height: '220%', transformOrigin: '50% 0', transform: 'rotateX(62deg)', backgroundImage: 'linear-gradient(rgba(255,51,198,.9) 2px, transparent 2px), linear-gradient(90deg, rgba(34,236,251,.7) 2px, transparent 2px)', backgroundSize: '44px 44px, 44px 44px', animation: 'prcGridMove 1.6s linear infinite' }} />
            <div style={{ ...abs, left: 0, right: 0, top: 0, height: '40%', background: 'linear-gradient(to bottom, #3a0a4a, rgba(0,0,0,0))' }} />
          </div>
        </Wrap>
      );
    case 'pulse':
      return (
        <Wrap kind={kind}>
          <div style={{ ...abs, inset: 10, border: '2px solid transparent', background: `${RING} border-box`, WebkitMask: 'linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude', backgroundSize: '200% 100%', opacity: 0.5, animation: 'prcRingSlide 10s linear infinite, prcGlow 4s ease-in-out infinite alternate' }} />
        </Wrap>
      );
    case 'signal':
      return (
        <Wrap kind={kind}>
          <div style={{ ...abs, inset: 0, animation: 'prcJitter 5s steps(1) infinite' }}>
            {BANDS.map(([y, hh, c, d], i) => (
              <div key={i} style={{ ...abs, left: '-40%', width: '180%', top: `${y}%`, height: `${hh}%`, background: `linear-gradient(90deg, ${c}00, ${c}, ${c}00)`, opacity: 0.45, mixBlendMode: 'screen', animation: `prcBand ${d}s ease-in-out ${-i}s infinite alternate` }} />
            ))}
          </div>
          <div style={{ ...abs, inset: 0, background: 'repeating-linear-gradient(0deg, rgba(0,0,0,.35) 0 2px, transparent 2px 4px)' }} />
        </Wrap>
      );
    case 'ink':
      return (
        <Wrap kind={kind} extra="#0B0B0B">
          <div style={{ ...abs, left: 0, right: 0, bottom: 0, height: 3, background: RING, backgroundSize: '200% 100%', animation: 'prcRingSlide 8s linear infinite' }} />
        </Wrap>
      );
    default:
      return <PortalBackground kind="lasers" />;
  }
}
